import { ShellHeader } from '@/app/shell-header';
import { TODAY } from '@/data/seed';
import { josa } from '@/lib/josa';
import { Button, cn, PageHeader } from '@mgrvtechgroup/admin-ds';
import { format, parseISO } from 'date-fns';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  average,
  bucketOf,
  bucketRanges,
  cellKey,
  lastWeeks,
  MATRIX_GROUPS,
  seedValues,
  WEEK_COUNT,
} from './matrix-model';
import { ValueSheet, type EditingCell } from './value-sheet';

const WEEKS = lastWeeks(TODAY);
const LEGEND = bucketRanges();

/**
 * 색 단계 5개 — 브랜드색(primary)의 농도만 바꾼 순차 팔레트. 흰 글자는 초록 위에서 대비가 모자라(약 2.6:1)
 * 모든 단계에 어두운 글자를 쓴다(가장 진한 단계에서도 약 6:1).
 * 색만으로 구분하지 않도록 셀에는 항상 숫자를 쓰고, 범례에는 구간 숫자를 병기한다.
 */
const BUCKET_CLASS = [
  'bg-primary/5 text-foreground',
  'bg-primary/20 text-foreground',
  'bg-primary/40 text-foreground',
  'bg-primary/70 text-foreground',
  'bg-primary text-foreground',
];

/** 행 높이 40px 고정(admin-ds D17) */
const ROW_HEIGHT = 40;
const LABEL_COLUMN = 176;
const GRID_COLUMNS = `${String(LABEL_COLUMN)}px repeat(${String(WEEK_COUNT)}, minmax(64px, 1fr))`;

const weekLabel = (week: string) => format(parseISO(week), 'M/d');
const weekTitle = (week: string) => format(parseISO(week), 'M월 d일 시작 주');

interface Hover {
  rowId: string | null;
  week: string;
}

/**
 * 매트릭스·히트맵 패턴 — 행(그룹 → 하위 행) × 열(최근 12주) 표에서 값의 크기를 색 단계와 숫자로 함께 보여 준다.
 * - 머리 행·첫 열 sticky, 마우스(또는 키보드 포커스)가 올라간 셀의 행·열을 십자로 강조한다.
 * - 그룹 행은 하위 행의 평균을 보여 주고 접고 펼 수 있다.
 * - 셀을 누르면 시트에서 값을 고친다(0~100 검증). 값은 메모리에만 남는다.
 *
 * 표는 div grid + ARIA table 역할로 그린다 — DataTable은 행 단위 데이터를 전제해 셀 단위 버튼·십자 강조를 표현할 수 없고,
 * raw <table>은 C26이 막기 때문에 ESLint 예외 없이 구조만 같은 의미로 만들었다.
 */
export function MatrixPage() {
  const [values, setValues] = useState(() => seedValues(MATRIX_GROUPS, WEEKS));
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [hover, setHover] = useState<Hover | null>(null);
  const [editing, setEditing] = useState<EditingCell | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const allCollapsed = collapsed.size === MATRIX_GROUPS.length;
  const toggleGroup = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) {
        next.add(id);
      }
      return next;
    });
  };

  const valueAt = (rowId: string, week: string) => values.get(cellKey(rowId, week)) ?? 0;

  const saveValue = (value: number) => {
    if (!editing) {
      return;
    }
    setValues((prev) => new Map(prev).set(cellKey(editing.rowId, editing.week), value));
    setSheetOpen(false);
    toast.success(
      `${editing.rowName} ${weekLabel(editing.week)} 주의 값을 ${josa(String(value), '으로/로')} 저장했습니다.`,
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ShellHeader>
        <PageHeader
          title="매트릭스·히트맵"
          description="그룹별 행과 주 단위 열에 값의 크기를 색 단계와 숫자로 보여 주고, 셀을 눌러 값을 고치는 페이지입니다."
        />
      </ShellHeader>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-[var(--layout-gutter)] py-3">
        <Button
          variant="outline"
          onClick={() => {
            setCollapsed(
              allCollapsed ? new Set() : new Set(MATRIX_GROUPS.map((group) => group.id)),
            );
          }}
        >
          {allCollapsed ? '모두 펼치기' : '모두 접기'}
        </Button>
        <ul className="flex flex-wrap items-center gap-x-3 gap-y-1" aria-label="색 단계 범례">
          {LEGEND.map(({ bucket, label }) => (
            <li key={bucket} className="caption text-muted-foreground flex items-center gap-1.5">
              <span
                aria-hidden
                className={cn('inline-block h-4 w-6 rounded-xs border', BUCKET_CLASS[bucket])}
              />
              {label}
            </li>
          ))}
        </ul>
      </div>

      <div className="min-h-0 flex-1 overflow-auto border-t">
        <div
          role="table"
          aria-label="그룹별 주간 값"
          aria-rowcount={MATRIX_GROUPS.length + 1}
          className="grid"
          style={{ gridTemplateColumns: GRID_COLUMNS, minWidth: LABEL_COLUMN + WEEK_COUNT * 64 }}
          onPointerLeave={() => {
            setHover(null);
          }}
        >
          <div role="row" className="contents">
            <div
              role="columnheader"
              className="bg-background caption text-muted-foreground sticky top-0 left-0 z-30 flex items-end border-r border-b px-3 py-2 font-medium"
            >
              그룹 / 행 · 주 시작일(월)
            </div>
            {WEEKS.map((week) => (
              <div
                key={week}
                role="columnheader"
                title={weekTitle(week)}
                className={cn(
                  'caption sticky top-0 z-20 flex items-end justify-end border-b px-2 py-2 font-medium tabular-nums',
                  hover?.week === week
                    ? 'bg-muted text-foreground'
                    : 'bg-background text-muted-foreground',
                )}
              >
                {weekLabel(week)}
              </div>
            ))}
          </div>

          {MATRIX_GROUPS.map((group) => {
            const open = !collapsed.has(group.id);
            return (
              <div key={group.id} role="rowgroup" className="contents">
                <div role="row" className="contents">
                  <div
                    role="rowheader"
                    className="bg-muted sticky left-0 z-10 border-r border-b"
                    style={{ height: ROW_HEIGHT }}
                  >
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => {
                        toggleGroup(group.id);
                      }}
                      className="label hover:bg-accent focus-visible:ring-ring flex size-full items-center gap-1.5 px-3 text-left focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                    >
                      {open ? (
                        <ChevronDownIcon className="size-4 shrink-0" aria-hidden />
                      ) : (
                        <ChevronRightIcon className="size-4 shrink-0" aria-hidden />
                      )}
                      {group.name}
                      <span className="caption text-muted-foreground font-normal">
                        {group.rows.length}개
                      </span>
                    </button>
                  </div>
                  {WEEKS.map((week) => {
                    const avg = average(group.rows.map((row) => valueAt(row.id, week)));
                    return (
                      <div
                        key={week}
                        role="cell"
                        title={`${group.name} 평균`}
                        className={cn(
                          'body flex items-center justify-end border-b px-2 font-medium tabular-nums',
                          avg === null ? 'bg-muted' : BUCKET_CLASS[bucketOf(avg)],
                          hover?.week === week && 'ring-foreground/40 ring-1 ring-inset',
                        )}
                        style={{ height: ROW_HEIGHT }}
                      >
                        <span className="sr-only">{`${group.name} 평균 `}</span>
                        {avg ?? '-'}
                      </div>
                    );
                  })}
                </div>

                {open &&
                  group.rows.map((row) => (
                    <div key={row.id} role="row" className="contents">
                      <div
                        role="rowheader"
                        className={cn(
                          'body sticky left-0 z-10 flex items-center border-r border-b pr-3 pl-9',
                          hover?.rowId === row.id ? 'bg-muted' : 'bg-background',
                        )}
                        style={{ height: ROW_HEIGHT }}
                      >
                        {row.name}
                      </div>
                      {WEEKS.map((week) => {
                        const value = valueAt(row.id, week);
                        const bucket = bucketOf(value);
                        const isHovered = hover?.rowId === row.id && hover.week === week;
                        const inCross = hover?.rowId === row.id || hover?.week === week;
                        return (
                          <div key={week} role="cell" className="border-b">
                            <button
                              type="button"
                              aria-label={`${group.name} ${row.name}, ${weekTitle(week)}, 값 ${String(value)}, ${LEGEND[bucket]?.label ?? ''} 구간. 눌러서 편집`}
                              onPointerEnter={() => {
                                setHover({ rowId: row.id, week });
                              }}
                              onFocus={() => {
                                setHover({ rowId: row.id, week });
                              }}
                              onBlur={() => {
                                setHover(null);
                              }}
                              onClick={() => {
                                setEditing({
                                  rowId: row.id,
                                  rowName: row.name,
                                  groupName: group.name,
                                  week,
                                  value,
                                });
                                setSheetOpen(true);
                              }}
                              className={cn(
                                'body focus-visible:ring-ring flex w-full items-center justify-end px-2 tabular-nums focus-visible:z-[5] focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset',
                                BUCKET_CLASS[bucket],
                                // 십자(같은 행·열)는 얇은 안쪽 선, 올린 셀은 굵은 선 — 색 단계와 겹쳐도 보이도록 배경이 아닌 테두리로 강조한다.
                                inCross && 'ring-foreground/40 ring-1 ring-inset',
                                isHovered && 'ring-foreground ring-2 ring-inset',
                              )}
                              style={{ height: ROW_HEIGHT }}
                            >
                              {value}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <ValueSheet
          cell={editing}
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          onSave={saveValue}
        />
      )}
    </div>
  );
}
